import { BreakpointObserver } from '@angular/cdk/layout'
import { NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  signal,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatTabsModule } from '@angular/material/tabs'
import { MatTooltipModule } from '@angular/material/tooltip'
import { PanelService } from '@components/panel/panel.service'
import { CMsTabComponent } from '@components/panel/tabs/cms-tab/cms-tab.component'
import { LayersTabComponent } from '@components/panel/tabs/layers-tab/layers-tab.component'
import { ResultsTabComponent } from '@components/panel/tabs/results-tab/results-tab.component'
import { ScenarioTabComponent } from '@components/panel/tabs/scenario-tab/scenario-tab.component'
import { ScenarioService } from '@components/panel/tabs/scenario-tab/scenario.service'
import { CalculationModuleService } from '@core/services/calculation-module.service'
import { LayerResultService } from '@core/services/layer-result.service'
import { environment } from '@env/environment'
import { map } from 'rxjs'

const MOBILE_QUERY = '(max-width: 1200px)'

@Component({
  selector: 'app-panel',
  standalone: true,
  templateUrl: './panel.component.html',
  styleUrl: './panel.component.scss',
  providers: [PanelService],
  imports: [
    // Components
    CMsTabComponent,
    LayersTabComponent,
    ScenarioTabComponent,
    ResultsTabComponent,
    // Directives
    NgTemplateOutlet,
    // Material
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelComponent implements OnDestroy {
  leftPanel = new PanelService()
  rightPanel = new PanelService()
  mobilePanel = new PanelService()

  calculationModuleService = inject(CalculationModuleService)
  scenarioService = inject(ScenarioService)
  private readonly _layerResultService = inject(LayerResultService)
  private readonly _breakpointObserver = inject(BreakpointObserver)

  readonly isMobile = toSignal(
    this._breakpointObserver.observe(MOBILE_QUERY).pipe(map((state) => state.matches)),
    { requireSync: true },
  )

  displayLayerTab = true
  displayCMTab = false
  displayScenarioTab = false
  //Using a separate ID allows for better management when the “Results” tab is selected on a mobile device and you then switch to the “Desktop” view.
  readonly leftSelectedTabIndex = signal<number>(0)
  readonly mobileSelectedTabIndex = signal<number>(0)

  get isProcessing() {
    return this.calculationModuleService.isCMRunning$() || this.scenarioService.isDataLoading$()
  }

  constructor() {
    this.leftPanel.openPanel()
    this.rightPanel.openPanel()
    this.mobilePanel.openPanel()

    effect(() => {
      // open the results panel automatically
      if (
        this.calculationModuleService.isCMRunning$() ||
        this._layerResultService.isLayerResultsLoading$()
      ) {
        this.rightPanel.openPanel()
      }
    })
  }

  ngOnInit() {
    if (environment.cmsId.length > 0) {
      this.displayCMTab = true
    }

    if (environment.scenarioExplorer !== '') {
      this.displayScenarioTab = true
    }
  }

  ngOnDestroy() {
    this.calculationModuleService.resetCurrentCM()
  }

  // Management of keeping ID when changing view (mobile/desktop)
  get tabCount() {
    let count = 0
    if (this.displayLayerTab) count++
    if (this.displayCMTab) count++
    if (this.displayScenarioTab) count++

    return count
  }
  onLeftTabIndexChange(index: number) {
    this.leftSelectedTabIndex.set(index)
    this.mobileSelectedTabIndex.set(index)
  }
  onMobileTabIndexChange(index: number) {
    this.mobileSelectedTabIndex.set(index)
    // don't mirror Results (mobile-only) onto the left group, it has no such tab
    if (index < this.tabCount) {
      this.leftSelectedTabIndex.set(index)
    }
  }
}
